import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import api from "../lib/api";
import ServerStatus from "../components/ServerStatus";
import AuthBrandPanel from "../components/AuthBrandPanel";
import { Mail, ArrowRight, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(err?.response?.data?.detail || "Too many requests — please wait a while and try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen grid grid-cols-1 lg:grid-cols-2">

      {/* ══════════════ LEFT — FORM ══════════════ */}
      <div className="flex items-center justify-center p-6 lg:p-14 bg-white">
        <div className="w-full max-w-[400px]">
          <Link to="/" className="lg:hidden mb-8 inline-block">
            <Logo className="h-9 w-auto" />
          </Link>

          <ServerStatus />

          {!sent ? (
            <>
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#0025D8] mb-3">RESET PASSWORD</div>
              <h1 className="font-display font-black text-[2.6rem] leading-[1.1] tracking-tight text-[#0F172A] mb-2">
                Forgot your<br />password?
              </h1>
              <p className="text-sm text-muted-foreground mb-8">
                Enter the email on your account and we'll send you a secure link to set a new one.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-border bg-white pl-10 pr-4 py-3 text-sm text-[#0F172A] placeholder:text-muted-foreground/50 focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/15 outline-none transition-all"
                      data-testid="forgot-email" />
                  </div>
                </div>

                <button type="submit" disabled={submitting} data-testid="forgot-submit"
                  className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] text-white py-3.5 font-semibold text-sm shadow-lg shadow-[#0025D8]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                    : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
              </div>
              <h1 className="font-display font-black text-[2.2rem] leading-[1.1] tracking-tight text-[#0F172A] mb-2">
                Check your inbox.
              </h1>
              <p className="text-sm text-muted-foreground mb-2">
                If an account exists for <span className="font-semibold text-[#0F172A]">{email}</span>, we've sent a link to reset your password.
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                The link expires in 1 hour. Don't see it? Check your spam folder, or{" "}
                <button onClick={() => setSent(false)} className="text-[#0025D8] font-semibold hover:underline">try a different email</button>.
              </p>
            </>
          )}

          <Link to="/login" className="inline-flex items-center gap-1.5 mt-8 text-sm font-semibold text-muted-foreground hover:text-[#0025D8] transition-colors group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" /> Back to login
          </Link>
        </div>
      </div>

      {/* ══════════════ RIGHT — BRAND PANEL ══════════════ */}
      <AuthBrandPanel
        eyebrow="ACCOUNT RECOVERY"
        title="Back to acing interviews in a minute."
        subtitle="Reset your password securely and pick up right where you left off."
      />
    </main>
  );
}
